// VNEC v9 — pages/hop-dong.js
// Header trong bảng hop_dong (1 row/HĐ), hạng mục trong hop_dong_items (N rows).
// Submit duyệt qua RPC submit_for_approval (không direct UPDATE trang_thai).
// Denorm columns (ma_hd, nha_thau, phan_loai_hd, hinh_thuc_hd, ma_du_an, loai_tt, ma_ho_so, thanh_tien)
// đã được thêm vào 006_v9_fixes.sql để giảm churn.
import { supabase, submitForApproval } from '../lib/supabase.js';
import { formatVND, parseVND, stateLabel, attachVNDFormat, fieldsetDisabled, esc } from '../lib/forms.js';
import { ok, err } from '../components/toast.js';
import { buildMaHD, getNextContractNumber, isMaHDExist, isNguyenTac } from '../lib/naming.js';

// ═══════════════════════════════════════════════════════════
// LIST — group by ma_hd, count items from hop_dong_items
// ═══════════════════════════════════════════════════════════
export async function renderHopDongList() {
  window.__pageInit = loadList;
  return `
    <div class="page">
      <div class="page-header">
        <h1>Hợp đồng</h1>
        <div class="page-actions">
          <input type="search" id="search" placeholder="Tìm mã HĐ, nhà thầu..." class="input-search">
          <select id="filter-state" class="input-sm">
            <option value="">Tất cả</option>
            <option value="draft">Nháp</option>
            <option value="pending_l1">Chờ duyệt L1</option>
            <option value="pending_l2">Chờ duyệt L2</option>
            <option value="approved">Đã duyệt</option>
            <option value="rejected">Từ chối</option>
          </select>
          <a href="#/hop-dong/new" class="btn btn-primary">+ Tạo HĐ</a>
        </div>
      </div>
      <div class="card">
        <div class="table-wrap">
          <table class="table" id="hd-table">
            <thead><tr>
              <th>Mã HĐ</th><th>Nhà thầu</th><th>Dự án</th>
              <th class="num">Tổng GT</th><th class="num">Số hạng mục</th>
              <th>Trạng thái</th><th></th>
            </tr></thead>
            <tbody><tr><td colspan="7" class="muted center">Đang tải...</td></tr></tbody>
          </table>
        </div>
      </div>
    </div>`;
}

async function loadList() {
  const tbody = document.querySelector('#hd-table tbody');
  const search = document.getElementById('search');
  const filterState = document.getElementById('filter-state');

  async function query() {
    tbody.innerHTML = '<tr><td colspan="7" class="muted center">Đang tải...</td></tr>';
    // PostgREST embed: count hop_dong_items cùng lúc
    let q = supabase.from('hop_dong')
      .select('id, ma_hd, so_hd, nha_thau, ma_du_an, gia_tri, trang_thai, phan_loai_hd, hop_dong_items(count)')
      .is('deleted_at', null).order('created_at', { ascending: false }).limit(500);
    if (filterState.value) q = q.eq('trang_thai', filterState.value);
    const { data, error } = await q;
    if (error) { tbody.innerHTML = `<tr><td colspan="7">Lỗi: ${error.message}</td></tr>`; return; }

    let rows = (data || []).map(r => ({
      id: r.id,
      ma_hd: r.ma_hd || r.so_hd,
      nha_thau: r.nha_thau || '',
      ma_du_an: r.ma_du_an || '',
      tongTT: Number(r.gia_tri || 0),
      count: r.hop_dong_items?.[0]?.count || 0,
      trang_thai: r.trang_thai,
      phan_loai_hd: r.phan_loai_hd || '',
      isHDNT: /hdnt|nguyen|nguyên/i.test(r.phan_loai_hd || '')
    }));

    if (search.value) {
      const s = search.value.toLowerCase();
      rows = rows.filter(r =>
        (r.ma_hd || '').toLowerCase().includes(s) || (r.nha_thau || '').toLowerCase().includes(s)
      );
    }

    if (!rows.length) { tbody.innerHTML = '<tr><td colspan="7" class="muted center">Không có HĐ.</td></tr>'; return; }

    // Load DH counts for HDNT rows in bulk
    const hdntIds = rows.filter(r => r.isHDNT).map(r => r.id);
    const dhCountMap = {};
    if (hdntIds.length) {
      const { data: dhs } = await supabase.from('don_hang')
        .select('hop_dong_id, trang_thai').in('hop_dong_id', hdntIds).is('deleted_at', null);
      (dhs || []).forEach(d => {
        if (!dhCountMap[d.hop_dong_id]) dhCountMap[d.hop_dong_id] = { total: 0, approved: 0 };
        dhCountMap[d.hop_dong_id].total++;
        if (d.trang_thai === 'approved') dhCountMap[d.hop_dong_id].approved++;
      });
    }

    tbody.innerHTML = rows.map(r => {
      const isApproved = r.trang_thai === 'approved';
      const dhInfo = dhCountMap[r.id];
      const hdntBadge = r.isHDNT
        ? `<span class="bg-st" style="background:#FFCC00;color:#000066;font-size:10px;padding:1px 5px;border-radius:3px;font-weight:bold;margin-left:4px">HDNT</span>`
        : '';
      const dhBadge = (r.isHDNT && dhInfo)
        ? ` <button class="btn btn-ghost btn-sm" data-act="view-dh" data-mahd="${esc(r.ma_hd)}" title="Xem đơn hàng dưới HĐ này" style="padding:1px 6px;font-size:11px">${dhInfo.approved}/${dhInfo.total} ĐH</button>`
        : '';
      const quickActions = isApproved
        ? `<button class="btn btn-ghost btn-sm" data-act="add-pl" data-mahd="${esc(r.ma_hd)}" title="Tạo phụ lục cho HĐ này" style="padding:1px 6px;font-size:11px;background:#EEEDFE;color:#3C3489;border-color:#AFA9EC">+ PL</button>
           <button class="btn btn-ghost btn-sm" data-act="add-pyc" data-mahd="${esc(r.ma_hd)}" title="Tạo PYC liên quan" style="padding:1px 6px;font-size:11px;background:#FAEEDA;color:#854F0B;border-color:#FAC775">+ PYC</button>
           ${r.isHDNT ? `<button class="btn btn-ghost btn-sm" data-act="add-dh" data-mahd="${esc(r.ma_hd)}" title="Tạo đơn hàng dưới HDNT này" style="padding:1px 6px;font-size:11px;background:#E1F5EE;color:#085041;border-color:#5DCAA5">+ ĐH</button>` : ''}`
        : '';
      return `<tr>
        <td class="small"><strong>${esc(r.ma_hd)}</strong>${hdntBadge}</td>
        <td>${esc(r.nha_thau)}</td>
        <td class="small">${esc(r.ma_du_an)}</td>
        <td class="num">${formatVND(r.tongTT)}</td>
        <td class="num">${r.count}${dhBadge}</td>
        <td>${stateLabel(r.trang_thai)}</td>
        <td><a href="#/hop-dong/${r.id}" class="btn btn-ghost btn-sm">Xem</a> ${quickActions}</td>
      </tr>`;
    }).join('');

    // Quick action handlers
    tbody.querySelectorAll('button[data-act]').forEach(btn => {
      btn.addEventListener('click', () => {
        const act = btn.dataset.act;
        const ma = btn.dataset.mahd;
        if (act === 'add-pl') {
          sessionStorage.setItem('vnec_prefill_parent_pl', ma);
          window.location.hash = '#/phu-luc';
        } else if (act === 'add-pyc') {
          sessionStorage.setItem('vnec_prefill_parent_pyc', ma);
          window.location.hash = '#/pyctt/new';
        } else if (act === 'add-dh') {
          sessionStorage.setItem('vnec_prefill_parent_dh', ma);
          window.location.hash = '#/don-hang';
        } else if (act === 'view-dh') {
          sessionStorage.setItem('vnec_filter_parent_dh', ma);
          window.location.hash = '#/don-hang';
        }
      });
    });
  }

  let t;
  search.addEventListener('input', () => { clearTimeout(t); t = setTimeout(query, 300); });
  filterState.addEventListener('change', query);
  query();
}

// ═══════════════════════════════════════════════════════════
// FORM — Multi-row hạng mục (v9: hop_dong_items bảng con)
// ═══════════════════════════════════════════════════════════
export async function renderHopDongForm(idParam) {
  window.__pageInit = () => initForm(idParam);
  return `
    <div class="page">
      <div class="page-header">
        <h1>${idParam ? 'Hợp đồng' : 'Tạo hợp đồng'}</h1>
        <a href="#/hop-dong" class="btn btn-ghost">← Danh sách</a>
      </div>
      <form id="hd-form" class="card form-card">
        <div id="form-header" class="form-header"></div>

        <div class="grid-2">
          <label class="field">
            <span>Mã NTP/NCC *</span>
            <select name="ma_ho_so" id="sel-ntp" required><option value="">-- Chọn --</option></select>
          </label>
          <label class="field">
            <span>Tên nhà thầu</span>
            <input name="nha_thau" id="inp-ncc" readonly>
          </label>
          <label class="field">
            <span>Phân loại HĐ *</span>
            <select name="phan_loai_hd" id="sel-phanloai" required>
              <option value="">-- Chọn --</option>
              <option value="GoiThau">Gói thầu</option>
              <option value="HDNT">HĐ Nguyên tắc</option>
              <option value="MuaBan">Mua bán</option>
              <option value="DichVu">Dịch vụ</option>
            </select>
          </label>
          <label class="field">
            <span>Hình thức *</span>
            <select name="hinh_thuc_hd" id="sel-hinhthuc" required>
              <option value="">-- Chọn --</option>
              <option value="TronGoi">Trọn gói</option>
              <option value="DonGia">Đơn giá</option>
              <option value="HonHop">Hỗn hợp</option>
            </select>
          </label>
          <label class="field">
            <span>Dự án *</span>
            <select name="ma_du_an" id="sel-duan" required><option value="">-- Chọn --</option></select>
          </label>
          <label class="field">
            <span>Mã HĐ (tự động)</span>
            <input name="ma_hd" id="inp-mahd" readonly>
          </label>
        </div>

        <div class="grid-2">
          <label class="field">
            <span>Mã HĐ thủ công (tùy chọn)</span>
            <input id="inp-mahd-custom" placeholder="Để trống = dùng mã auto">
          </label>
          <div id="tt-badge-wrap" style="padding-top:22px">
            <span id="tt-badge" class="badge"></span>
          </div>
        </div>

        <div class="table-wrap" id="items-wrap">
          <table class="table table-compact">
            <colgroup>
              <col style="width:36px">
              <col>
              <col style="width:70px">
              <col style="width:90px">
              <col style="width:130px">
              <col style="width:150px">
              <col style="width:180px">
              <col style="width:42px">
            </colgroup>
            <thead><tr>
              <th>#</th>
              <th>Nội dung *</th>
              <th>ĐV</th>
              <th class="num">SL</th>
              <th class="num">Đơn giá</th>
              <th class="num">Thành tiền</th>
              <th>Loại CP *</th>
              <th></th>
            </tr></thead>
            <tbody id="tbody-items"></tbody>
          </table>
        </div>
        <div class="table-footer">
          <button type="button" class="btn btn-ghost btn-sm" id="btn-add-row">+ Thêm dòng</button>
          <div class="total-box">Tổng: <strong id="total-display">0</strong> VNĐ</div>
        </div>

        <div class="form-actions" id="form-actions">
          <button type="button" class="btn btn-primary" data-action="save">Lưu nháp</button>
          <button type="button" class="btn btn-success" data-action="submit">Lưu & Gửi duyệt</button>
        </div>
      </form>
    </div>`;
}

let _rowCount = 0;
let _loaiCPList = [];
let _nccMap = {};
let _editId = null;

async function initForm(idParam) {
  _editId = idParam || null;
  _rowCount = 0;

  const form = document.getElementById('hd-form');
  const headerEl = document.getElementById('form-header');
  const actionsEl = document.getElementById('form-actions');
  const selNTP = document.getElementById('sel-ntp');
  const selPhanLoai = document.getElementById('sel-phanloai');
  const selHinhThuc = document.getElementById('sel-hinhthuc');
  const selDuAn = document.getElementById('sel-duan');
  const inpMaHD = document.getElementById('inp-mahd');
  const inpMaHDCustom = document.getElementById('inp-mahd-custom');
  const inpNCC = document.getElementById('inp-ncc');
  const badge = document.getElementById('tt-badge');
  const tbody = document.getElementById('tbody-items');

  // Load catalogs (v9 real tables)
  const [nccRes, duAnRes, lcpRes] = await Promise.all([
    supabase.from('dm_ho_so').select('ma_ho_so, mo_ta, nha_thau').eq('is_active', true).order('sort_order'),
    supabase.from('du_an').select('ma_da, ten_da').eq('trang_thai', 'active').is('deleted_at', null).order('ma_da'),
    supabase.from('ifrs_rules').select('loai_cp').order('loai_cp')
  ]);

  (nccRes.data || []).forEach(h => {
    const display = h.nha_thau || h.mo_ta || h.ma_ho_so;
    selNTP.insertAdjacentHTML('beforeend', `<option value="${h.ma_ho_so}">${h.ma_ho_so} — ${esc(display)}</option>`);
    _nccMap[h.ma_ho_so] = h.nha_thau || h.mo_ta || '';
  });
  (duAnRes.data || []).forEach(d =>
    selDuAn.insertAdjacentHTML('beforeend', `<option value="${d.ma_da}">${d.ma_da} — ${esc(d.ten_da)}</option>`));
  _loaiCPList = (lcpRes.data || []).map(x => x.loai_cp);

  // v3: Auto-fill + lock project cho data_entry
  try {
    const { getCurrentProject } = await import('../lib/session.js');
    const { getCurrentUser } = await import('../lib/supabase.js');
    const u = await getCurrentUser();
    const curProj = await getCurrentProject();
    if (curProj && (u.role === 'data_entry' || u.role === 'viewer')) {
      selDuAn.value = curProj;
      selDuAn.disabled = true;
      selDuAn.style.background = '#EEEDFE';
      selDuAn.style.color = '#534AB7';
      selDuAn.style.fontWeight = '700';
      selDuAn.title = 'Dự án được khóa theo phiên làm việc. Đổi dự án bằng menu ở sidebar.';
    } else if (curProj) {
      // Admin/approver: pre-select nhưng cho phép đổi
      selDuAn.value = curProj;
    }
  } catch (e) { /* silent */ }

  // ── Naming preview ─────────────────────────────────────
  let _nextSTT = null;
  async function genCode() {
    if (_editId) return;
    const maNTP = selNTP.value;
    const phanLoai = selPhanLoai.value;
    const hinhThuc = selHinhThuc.value;
    const maDuAn = selDuAn.value;
    if (_nextSTT === null) {
      try { _nextSTT = await getNextContractNumber(); } catch (e) { _nextSTT = 1; }
    }
    inpMaHD.value = buildMaHD({ stt: _nextSTT, maNTP, phanLoai, hinhThuc, maDuAn });
    // v9.1: HĐ Nguyên tắc → ẨN bảng hạng mục (chỉ lưu header, hạng mục đi qua Đơn hàng)
    const isNT = isNguyenTac(phanLoai);
    const itemsWrap = document.getElementById('items-wrap');
    const tableFooter = document.querySelector('.table-footer');
    if (isNT) {
      badge.textContent = '📋 HĐ Nguyên tắc — KHÔNG có hạng mục. Hạng mục được tạo qua Đơn hàng (DH)';
      badge.className = 'badge badge-warning';
      if (itemsWrap) itemsWrap.style.display = 'none';
      if (tableFooter) tableFooter.style.display = 'none';
    } else if (phanLoai) {
      badge.textContent = '📎 HĐ Gộp — dùng Phụ lục';
      badge.className = 'badge badge-info';
      if (itemsWrap) itemsWrap.style.display = '';
      if (tableFooter) tableFooter.style.display = '';
    } else {
      badge.textContent = '';
      badge.className = 'badge';
      if (itemsWrap) itemsWrap.style.display = '';
      if (tableFooter) tableFooter.style.display = '';
    }
  }

  selNTP.addEventListener('change', () => {
    inpNCC.value = _nccMap[selNTP.value] || '';
    genCode();
  });
  selPhanLoai.addEventListener('change', genCode);
  selHinhThuc.addEventListener('change', genCode);
  selDuAn.addEventListener('change', genCode);

  // ── Items table ────────────────────────────────────────
  function addRow(data = {}) {
    _rowCount++;
    const lcpOpts = '<option value="">--</option>' +
      _loaiCPList.map(v => `<option value="${v}" ${data.loai_cp === v ? 'selected' : ''}>${v}</option>`).join('');
    const tr = document.createElement('tr');
    tr.id = 'row-' + _rowCount;
    tr.dataset.itemId = data.id || '';  // track existing item for update vs insert
    tr.innerHTML = `
      <td class="num small">${_rowCount}</td>
      <td><input name="noi_dung" required value="${esc(data.noi_dung || '')}"></td>
      <td><input name="don_vi" value="${esc(data.don_vi || '')}"></td>
      <td><input name="so_luong" inputmode="numeric" value="${data.so_luong ? formatVND(data.so_luong) : ''}"></td>
      <td><input name="don_gia" inputmode="numeric" value="${data.don_gia ? formatVND(data.don_gia) : ''}"></td>
      <td><input name="thanh_tien" inputmode="numeric" readonly class="bold" value="${data.thanh_tien ? formatVND(data.thanh_tien) : '0'}"></td>
      <td><select name="loai_cp" required>${lcpOpts}</select></td>
      <td><button type="button" class="btn-icon" data-del="${_rowCount}" title="Xóa">✕</button></td>
    `;
    tbody.appendChild(tr);
    const inpSL = tr.querySelector('[name=so_luong]');
    const inpDG = tr.querySelector('[name=don_gia]');
    const inpTT = tr.querySelector('[name=thanh_tien]');
    attachVNDFormat(inpSL);
    attachVNDFormat(inpDG);
    const calc = () => {
      const sl = parseVND(inpSL.value);
      const dg = parseVND(inpDG.value);
      inpTT.value = formatVND(sl * dg);
      updateTotal();
    };
    inpSL.addEventListener('input', calc);
    inpDG.addEventListener('input', calc);
    updateTotal();
  }

  function updateTotal() {
    let total = 0;
    tbody.querySelectorAll('[name=thanh_tien]').forEach(i => total += parseVND(i.value));
    document.getElementById('total-display').textContent = formatVND(total);
  }

  document.getElementById('btn-add-row').addEventListener('click', () => addRow());
  tbody.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-del]');
    if (!btn) return;
    document.getElementById('row-' + btn.dataset.del).remove();
    updateTotal();
  });

  // ── Edit mode: load existing header + items ───────────
  let _existingHD = null;
  if (_editId) {
    const { data: hd, error } = await supabase.from('hop_dong')
      .select('*, hop_dong_items(*)').eq('id', _editId).is('deleted_at', null).maybeSingle();
    if (error || !hd) { err('Không tìm thấy HĐ: ' + (error?.message || _editId)); return; }
    _existingHD = hd;

    inpMaHD.value = hd.ma_hd || hd.so_hd || '';
    selNTP.value = hd.ma_ho_so || '';
    inpNCC.value = hd.nha_thau || hd.nha_cung_cap || '';
    selPhanLoai.value = hd.phan_loai_hd || '';
    selHinhThuc.value = hd.hinh_thuc_hd || '';
    selDuAn.value = hd.ma_du_an || '';
    [selNTP, selPhanLoai, selHinhThuc, selDuAn].forEach(s => s.disabled = true);
    document.getElementById('inp-mahd-custom').closest('.field').style.display = 'none';

    const items = (hd.hop_dong_items || []).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    if (items.length) items.forEach(it => addRow(it));
    else addRow();

    headerEl.innerHTML = `${stateLabel(hd.trang_thai)} <span class="muted">• ${hd.ma_hd || hd.so_hd}</span>`;
    if (!['draft', 'rejected'].includes(hd.trang_thai)) {
      fieldsetDisabled(form, true);
      actionsEl.innerHTML = '<span class="muted">HĐ đã vào quy trình — không thể chỉnh sửa.</span>';
    }
    if (hd.reject_reason) {
      headerEl.innerHTML += `<div class="alert alert-warning">Lý do từ chối: ${esc(hd.reject_reason)}</div>`;
    }
  } else {
    addRow(); addRow(); addRow();
    genCode();
  }

  // ── Save ────────────────────────────────────────────────
  form.addEventListener('submit', e => e.preventDefault());
  actionsEl.addEventListener('click', async (e) => {
    const btn = e.target.closest('button[data-action]');
    if (!btn) return;

    // Validate header
    if (!_editId) {
      if (!selNTP.value) { err('Thiếu Mã NTP/NCC'); return; }
      if (!selPhanLoai.value) { err('Thiếu Phân loại HĐ'); return; }
      if (!selHinhThuc.value) { err('Thiếu Hình thức'); return; }
      if (!selDuAn.value) { err('Thiếu Dự án'); return; }
    }

    const maHD = _editId ? (_existingHD.ma_hd || _existingHD.so_hd) : (inpMaHDCustom.value.trim() || inpMaHD.value);
    if (!maHD || maHD.includes('...')) { err('Mã HĐ chưa đầy đủ'); return; }

    // Check duplicate (only for new)
    if (!_editId) {
      try {
        if (await isMaHDExist(maHD)) {
          err(`Mã HĐ [${maHD}] đã tồn tại. Dùng mã thủ công khác hoặc sửa HĐ cũ.`);
          return;
        }
      } catch (e) { err(e.message); return; }
    }

    // Collect items
    const isNT = isNguyenTac(selPhanLoai.value);
    const items = [];
    let hasErr = false;
    if (!isNT) {
      [...tbody.querySelectorAll('tr')].forEach((tr, idx) => {
        const nd = tr.querySelector('[name=noi_dung]').value.trim();
        const lcp = tr.querySelector('[name=loai_cp]').value;
        if (!nd) return;
        if (!lcp) { hasErr = true; return; }
        items.push({
          id: tr.dataset.itemId || null,
          sort_order: idx,
          noi_dung: nd,
          don_vi: tr.querySelector('[name=don_vi]').value || null,
          so_luong: parseVND(tr.querySelector('[name=so_luong]').value),
          don_gia: parseVND(tr.querySelector('[name=don_gia]').value),
          loai_cp: lcp
        });
      });

      if (hasErr) { err('Có dòng thiếu Loại CP'); return; }
      if (!items.length) { err('Chưa có hạng mục nào'); return; }
    }
    // HDNT: items rỗng — chỉ lưu header, gia_tri = 0 (sẽ update từ Đơn hàng)

    const tongTT = items.reduce((s, it) => s + Number(it.so_luong) * Number(it.don_gia), 0);

    const headerPayload = {
      ma_hd: maHD,
      ma_ho_so: selNTP.value,
      nha_thau: inpNCC.value || null,
      phan_loai_hd: selPhanLoai.value,
      hinh_thuc_hd: selHinhThuc.value,
      ma_du_an: selDuAn.value,
      loai_tt: isNguyenTac(selPhanLoai.value) ? 'don_hang' : 'gop',
      gia_tri: tongTT,
      thanh_tien: tongTT,
      trang_thai: 'draft'
      // so_hd, ten_hd, created_by: filled by hop_dong_sync_ma_hd trigger
    };

    fieldsetDisabled(form, true);
    btn.textContent = 'Đang lưu...';

    try {
      let savedId = _editId;

      if (_editId) {
        // UPDATE header (only allowed if state in draft/rejected — RLS enforces)
        const { error: uErr } = await supabase.from('hop_dong').update({
          ma_ho_so: headerPayload.ma_ho_so,
          nha_thau: headerPayload.nha_thau,
          phan_loai_hd: headerPayload.phan_loai_hd,
          hinh_thuc_hd: headerPayload.hinh_thuc_hd,
          ma_du_an: headerPayload.ma_du_an,
          loai_tt: headerPayload.loai_tt,
          gia_tri: tongTT,
          thanh_tien: tongTT
        }).eq('id', _editId);
        if (uErr) throw uErr;

        // Items: simplest safe pattern — delete all, insert all
        // RLS cho phép delete nếu hop_dong.created_by=self AND state in (draft,rejected)
        const { error: dErr } = await supabase.from('hop_dong_items').delete().eq('hop_dong_id', _editId);
        if (dErr) throw dErr;

        const itemRows = items.map(it => ({
          hop_dong_id: _editId,
          sort_order: it.sort_order,
          noi_dung: it.noi_dung,
          don_vi: it.don_vi,
          so_luong: it.so_luong,
          don_gia: it.don_gia,
          loai_cp: it.loai_cp
        }));
        if (itemRows.length) {
          const { error: iErr } = await supabase.from('hop_dong_items').insert(itemRows);
          if (iErr) throw iErr;
        }
      } else {
        // INSERT new header
        const { data: newHD, error: cErr } = await supabase.from('hop_dong')
          .insert(headerPayload).select('id').single();
        if (cErr) throw cErr;
        savedId = newHD.id;

        const itemRows = items.map(it => ({
          hop_dong_id: savedId,
          sort_order: it.sort_order,
          noi_dung: it.noi_dung,
          don_vi: it.don_vi,
          so_luong: it.so_luong,
          don_gia: it.don_gia,
          loai_cp: it.loai_cp
        }));
        if (itemRows.length) {
          const { error: iErr } = await supabase.from('hop_dong_items').insert(itemRows);
          if (iErr) throw iErr;
        }
      }

      if (btn.dataset.action === 'submit') {
        await submitForApproval('hop_dong', savedId);
        ok('Đã lưu & gửi duyệt: ' + maHD);
      } else {
        ok('Đã lưu nháp: ' + maHD);
      }
      setTimeout(() => window.location.hash = '#/hop-dong', 600);
    } catch (e) {
      err('Lỗi: ' + e.message);
      fieldsetDisabled(form, false);
      btn.textContent = btn.dataset.action === 'submit' ? 'Lưu & Gửi duyệt' : 'Lưu nháp';
    }
  });
}
